import {
  CheckCircle2,
  FileText,
  Loader2,
  ThumbsDown,
  ThumbsUp,
  XCircle,
} from 'lucide-react';
import React, { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { useMatter } from '@/hooks/useMatter';
import type {
  FilevineCreateNoteActionSchema,
  FilevineSyncActionStatus,
} from '@/services/api/generated/filevineSyncService';
import { enhancedApi } from '@/services/api/generated/filevineSyncService';

interface FilevineCreateNoteActionProps {
  action: FilevineCreateNoteActionSchema;
  isStale: boolean;
}

const getStatusBadge = (status: FilevineSyncActionStatus) => {
  switch (status) {
    case 'EXECUTING':
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-blue-50 text-blue-700 rounded">
          <Loader2 className="h-3 w-3 animate-spin" />
          Executing
        </span>
      );
    case 'APPROVED':
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-green-50 text-green-700 rounded">
          <CheckCircle2 className="h-3 w-3" />
          Approved
        </span>
      );
    case 'FAILED':
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-red-50 text-red-700 rounded">
          <XCircle className="h-3 w-3" />
          Failed
        </span>
      );
    case 'COMPLETED':
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-green-50 text-green-700 rounded">
          <CheckCircle2 className="h-3 w-3" />
          Completed
        </span>
      );
    case 'REJECTED':
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-red-50 text-red-700 rounded">
          <XCircle className="h-3 w-3" />
          Rejected
        </span>
      );
    default:
      return null;
  }
};

export const FilevineCreateNoteAction: React.FC<
  FilevineCreateNoteActionProps
> = ({ action, isStale }) => {
  const { matterId } = useMatter();
  const [isUpdating, setIsUpdating] = useState(false);

  const [updateStatus] =
    enhancedApi.useFilevineSyncActionUpdateStatusMutation();

  const handleStatusChange = async (newStatus: FilevineSyncActionStatus) => {
    setIsUpdating(true);
    try {
      await updateStatus({
        matterId,
        actionId: action.id,
        patchedUpdateActionStatusSchema: { status: newStatus },
      }).unwrap();
      toast.success(`Action ${newStatus.toLowerCase()}`);
    } catch (error) {
      toast.error(`Failed to ${newStatus.toLowerCase()} action`);
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className="group px-4 py-3 flex items-start justify-between hover:bg-gray-50">
      {/* Note info */}
      <div className="flex items-start gap-3 flex-1 min-w-0">
        {/* Note icon */}
        <div className="flex-shrink-0 mt-0.5">
          <FileText className="h-4 w-4 text-purple-600" />
        </div>

        {/* Note content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <p className="text-sm font-medium text-gray-900">
              {action.subject || 'Note (no subject)'}
            </p>
            {getStatusBadge(action.status)}
          </div>
          <p className="text-xs text-gray-500 mb-2">Create note in Filevine</p>
          {action.body && (
            <div className="text-sm text-gray-700 bg-gray-50 rounded p-2 border border-gray-200">
              <p className="whitespace-pre-wrap line-clamp-3">{action.body}</p>
            </div>
          )}
          {action.error_message && (
            <div className="mt-2 text-xs text-red-600 bg-red-50 rounded p-2 border border-red-200">
              {action.error_message}
            </div>
          )}
        </div>
      </div>

      {/* Action buttons */}
      {action.status === 'AWAITING_APPROVAL' && (
        <div className="flex gap-2 ml-4 flex-shrink-0">
          <Button
            onClick={() => handleStatusChange('APPROVED')}
            disabled={isUpdating || isStale}
            variant="ghost"
            size="sm"
            className="h-8 px-3"
          >
            <ThumbsUp className="h-3.5 w-3.5 mr-1" />
            Approve
          </Button>
          <Button
            onClick={() => handleStatusChange('REJECTED')}
            disabled={isUpdating || isStale}
            variant="ghost"
            size="sm"
            className="h-8 px-3 text-red-600 hover:text-red-700 hover:bg-red-50"
          >
            <ThumbsDown className="h-3.5 w-3.5 mr-1" />
            Reject
          </Button>
        </div>
      )}
    </div>
  );
};
